<template>
  <div class="sp-quantum-select-accordion-container" @keydown="handleKeyDown">
    <Panel
      v-bind="panelForwardProps"
      :items="groupedItems"
      :search="search"
      :caret-variant="props.accordionCaretVariant"
      :caret-toggle="true"
      @update-search="handleUpdateSearch"
      @update-selected="handleUpdateSelected"
      @select-item="handleSelectItem"
      @next="handleNext"
    />
  </div>
</template>

<script setup>
/**
 * Navigation container selected by `navigation="accordion"`. Renders a single
 * Panel whose flat list splices children of expanded top-level rows in place,
 * single level only. Children of expanded parents are treated as leaves even
 * when their data declares `hasChildren: true`. The host wraps this container
 * in NavigationContainerShell; the accordion has no back action so it never
 * emits `update:on-back`.
 */
import { computed, reactive, ref, toRef, watch } from "vue";
import {
  forwardablePanelProps,
  panelProps,
  props as quantumSelectProps,
} from "#ce/components/SpQuantumSelect/props.js";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { booleanAttribute, toBoolean } from "#ce/utils/props";
import { useContainerSearch } from "#ce/components/SpQuantumSelect/composables/useContainerSearch.js";
import { useQuantumSelectItems } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems";
import Panel from "./QuantumSelectPanel.ce.vue";
import childIndicatorSvg from "./child-indicator.svg?raw";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["update-selected", "update-search", "update-current-item", "select-item"]);

const props = defineProps({
  ...panelProps,

  // Disables client-side filtering.
  noFilter: booleanAttribute(),
  accordionCaretVariant: quantumSelectProps.accordionCaretVariant,
  loadChildren: {
    type: Function,
    required: false,
    default: undefined,
  },
});

const noFilter = computed(() => toBoolean(props.noFilter));

const { getItemTitle, getItemValue } = useSpList(props);

const { search, applySearchInput } = useContainerSearch(props, emit);

function handleUpdateSearch(value) {
  applySearchInput(value);
}

// Expanded top-level node ids (set of item values). Updated by assignment so
// shallow watchers downstream observe the change.
const expanded = ref(new Set());

// Insertion-ordered list of expanded ids: last entry is the most recently
// expanded item, used to drive `update-current-item`.
const expandedOrder = ref([]);

// value → resolved children array. Lifetime is the component instance, so
// `loadChildren` is called at most once per top-level node.
const childrenCache = reactive(new Map());

// value → true while a `loadChildren` promise is in flight, so a rapid
// double-click on the same parent does not start a second load.
const loading = reactive(new Set());

// Snapshot of the user-chosen expanded set saved when a non-empty search
// begins; restored when the search clears so search-driven auto-expansion
// does not leak into the user's manual state.
const savedExpanded = ref(null);

function handleUpdateSelected(value) {
  emit("update-selected", value);
}

function handleSelectItem(item) {
  emit("select-item", item);
}

function emitCurrentItem() {
  const lastValue = expandedOrder.value.at(-1);
  if (lastValue == null) {
    emit("update-current-item", null);
    return;
  }
  const item = topLevelByValue(lastValue);
  emit("update-current-item", item ?? null);
}

function topLevelByValue(value) {
  return (props.items ?? []).find((item) => !item?.header && getItemValue(item) === value);
}

function isExpanded(value) {
  return expanded.value.has(value);
}

function setExpanded(next) {
  expanded.value = new Set(next);
}

function setLoading(value, isLoading) {
  if (isLoading) {
    loading.add(value);
  } else {
    loading.delete(value);
  }
}

async function resolveChildren(item) {
  if (props.loadChildren) {
    return await props.loadChildren(item);
  }
  return Array.isArray(item.children) ? item.children : [];
}

async function handleNext(item) {
  if (!item?.hasChildren) {
    return;
  }
  const value = getItemValue(item);
  // Defence in depth: only top-level items expand. A child rendered under an
  // expanded parent has had `hasChildren` stripped before reaching Panel, so
  // Panel never emits `next` for it; but if something ever forwards a stray
  // event for a non-top-level item we drop it on the floor instead of
  // calling loadChildren or mutating state for an item we do not own.
  if (!topLevelByValue(value)) {
    return;
  }
  if (loading.has(value)) {
    return;
  }

  if (isExpanded(value)) {
    const next = new Set(expanded.value);
    next.delete(value);
    setExpanded(next);
    expandedOrder.value = expandedOrder.value.filter((v) => v !== value);
    emitCurrentItem();
    return;
  }

  if (!childrenCache.has(value)) {
    setLoading(value, true);
    try {
      const children = await resolveChildren(item);
      childrenCache.set(value, children ?? []);
    } finally {
      setLoading(value, false);
    }
  }

  const next = new Set(expanded.value);
  next.add(value);
  setExpanded(next);
  expandedOrder.value = [...expandedOrder.value.filter((v) => v !== value), value];
  emitCurrentItem();
}

// Eagerly populate the children cache for items whose `children` array ships
// inline, so search auto-expand can find matches before the user clicks.
// Hosts that provide `loadChildren` stay lazy: the callback is the source of
// truth there, so we never seed from `children` even if a fixture supplies
// both.
function seedCacheFromInlineChildren(items) {
  if (props.loadChildren) {
    return;
  }
  for (const item of items ?? []) {
    if (!item?.hasChildren) {
      continue;
    }
    if (!Array.isArray(item.children)) {
      continue;
    }
    const value = getItemValue(item);
    if (childrenCache.has(value)) {
      continue;
    }
    childrenCache.set(value, item.children);
  }
}

watch(() => props.items, seedCacheFromInlineChildren, { immediate: true });

function autoExpandedSetFor(query) {
  const base = new Set(savedExpanded.value ?? expanded.value);
  if (!query) {
    return base;
  }
  const lowered = query.toLowerCase();
  for (const item of props.items ?? []) {
    if (!item?.hasChildren) {
      continue;
    }
    const value = getItemValue(item);
    const cached = childrenCache.get(value);
    if (!cached?.length) {
      continue;
    }
    const matches = cached.some((child) => getItemTitle(child)?.toLowerCase().includes(lowered));
    if (matches) {
      base.add(value);
    }
  }
  return base;
}

function applySearchExpansion(next, prev) {
  const becameNonEmpty = !prev && next;
  const becameEmpty = prev && !next;
  if (becameNonEmpty && savedExpanded.value === null) {
    savedExpanded.value = new Set(expanded.value);
  }
  if (becameEmpty && savedExpanded.value !== null) {
    setExpanded(savedExpanded.value);
    savedExpanded.value = null;
    return;
  }
  if (next) {
    setExpanded(autoExpandedSetFor(next));
  }
}

// Immediate so a host that mounts with `search` already set still triggers
// auto-expansion of cached parents whose children match.
watch(search, applySearchExpansion, { immediate: true });

// Top-level parent values whose children include a currently-selected value.
function selectionParentValues() {
  const selected = new Set((props.selected ?? []).map((value) => String(value)));
  if (!selected.size) {
    return [];
  }
  const parents = [];
  for (const item of props.items ?? []) {
    if (!item?.hasChildren) {
      continue;
    }
    const value = getItemValue(item);
    const children = childrenCache.get(value) ?? (Array.isArray(item.children) ? item.children : []);
    if (children.some((child) => selected.has(String(getItemValue(child))))) {
      parents.push(value);
    }
  }
  return parents;
}

function expandSelectionParents() {
  if (search.value) {
    return;
  }
  const parents = selectionParentValues();
  if (!parents.length) {
    return;
  }
  const next = new Set(expanded.value);
  parents.forEach((value) => next.add(value));
  setExpanded(next);
}

// Auto-expand the branch(es) holding the current selection so a chosen
// subspecialty is visible when the menu (re)opens. `start-on-root` tears the
// container down per open, so this immediate watch re-applies on every reopen.
// Skipped while searching — the `search` watch above owns expansion then.
watch([() => props.items, () => props.selected], expandSelectionParents, { immediate: true });

// Flat row list with children spliced beneath their expanded parents.
// Header rows pass through; children are stripped of `hasChildren` and tagged
// with a child-row class so Panel renders them as ordinary leaves.
const flatItems = computed(() => {
  const out = [];
  for (const item of props.items ?? []) {
    if (item?.header) {
      out.push(item);
      continue;
    }
    if (item?.hasChildren) {
      out.push(decorateParent(item));
      if (isExpanded(getItemValue(item))) {
        const children = childrenCache.get(getItemValue(item)) ?? [];
        for (const child of children) {
          out.push(asAccordionChild(child));
        }
      }
      continue;
    }
    out.push(item);
  }
  return out;
});

function decorateParent(item) {
  const value = getItemValue(item);
  const expandedNow = isExpanded(value);
  const loadingNow = loading.has(value);
  return {
    ...item,
    // Flag the row's expansion so Panel renders the append caret (SpCaret)
    // pointing up ("▲") when expanded and down ("▼") when collapsed. SpCaret
    // owns the rotation/transition, replacing the former
    // `--sp-comp-list-item-append-icon-rotation` inline-style hook.
    expanded: expandedNow,
    props: {
      ...(item.props ?? {}),
      loading: loadingNow,
      class: appendClass(item.props?.class, ["--accordion-parent", expandedNow ? "--accordion-expanded" : null]),
    },
  };
}

// Faint corner-arrow (↳) marking a row as a nested child, matching the legacy
// Spotlight `child-indicator`. The markup lives in child-indicator.svg, imported
// as a raw string and inlined as a data URI (not a sprite) so the indicator is
// self-contained; sp-icon fills it with the list-item prepend-icon colour.
// encodeURIComponent keeps the markup valid inside the `data:` URL.
// The glyph sits high in its viewBox (translate(0 -33.5)) so the box-centred
// sp-icon render lands the arrow's bottom stroke on the row text's baseline,
// pointing into the text line — the legacy indicator's optical alignment.
const CHILD_INDICATOR_ICON_URL = `data:image/svg+xml,${encodeURIComponent(childIndicatorSvg.trim())}`;

function asAccordionChild(child) {
  const copy = { ...child };
  delete copy.hasChildren;
  const childProps = { ...(child.props ?? {}) };
  childProps.class = appendClass(child.props?.class, ["--accordion-child"]);
  // Show the child indicator unless the child supplies its own prepend content.
  if (childProps.prependIcon == null && childProps.prependIconUrl == null) {
    childProps.prependIconUrl = CHILD_INDICATOR_ICON_URL;
  }
  // Indent the child's content one level deeper than its parent *without*
  // moving the row divider. Inline style (a CSS var) is the only path that
  // crosses the sp-list shadow boundary for a single host. We drive the prepend
  // column's left inset rather than padding the list-item host: host padding
  // shifts the whole grid — divider included — insetting the underline, whereas
  // the divider is positioned independently via `--divider-left`, so it stays
  // full-width (matching the legacy rows). The var is a host-level override.
  childProps.style = mergeStyle(child.props?.style, {
    "--sp-comp-list-item-prepend-margin-left":
      "var(--sp-comp-quantum-select-accordion-child-indent, var(--sp-ref-spacing-24, 3rem))",
  });
  copy.props = childProps;
  return copy;
}

function mergeStyle(existing, additions) {
  if (existing == null) {
    return additions;
  }
  if (typeof existing === "string") {
    const tail = Object.entries(additions)
      .map(([key, value]) => `${cssKey(key)}: ${value}`)
      .join("; ");
    return existing.endsWith(";") ? `${existing} ${tail}` : `${existing}; ${tail}`;
  }
  return { ...existing, ...additions };
}

function cssKey(key) {
  return key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

function appendClass(existing, additions) {
  const result = [];
  if (Array.isArray(existing)) {
    result.push(...existing);
  } else if (existing) {
    result.push(existing);
  }
  for (const cls of additions) {
    if (cls) {
      result.push(cls);
    }
  }
  return result;
}

// Augmented title used only for filtering and grouping in the composable:
// parents pseudo-match on their children's titles so a matching collapsed
// child keeps its parent visible after auto-expansion.
function effectiveTitle(item) {
  const own = getItemTitle(item) ?? "";
  if (!item?.hasChildren) {
    return own;
  }
  const value = getItemValue(item);
  const cached = childrenCache.get(value);
  if (!cached?.length) {
    return own;
  }
  const childTitles = cached
    .map((child) => getItemTitle(child))
    .filter(Boolean)
    .join(" ");
  return childTitles ? `${own} ${childTitles}` : own;
}

const { groupedItems } = useQuantumSelectItems({
  items: flatItems,
  search,
  noFilter,
  getItemTitle: effectiveTitle,
  maxResults: toRef(props, "maxResults"),
  fuzzySearch: computed(() => toBoolean(props.fuzzySearch)),
  highlightFirstResult: computed(() => toBoolean(props.highlightFirstResult)),
});

const panelForwardProps = computed(() => forwardablePanelProps(props));

// The keydown event re-targets to the sp-list host as it exits sp-list's
// shadow root, so event.target is not the focused row. composedPath() preserves
// the original path across shadow boundaries; the innermost entry is the row.
function focusedListItemFromEvent(event) {
  const path = event.composedPath?.() ?? [];
  for (const node of path) {
    if (node?.tagName?.toLowerCase?.() === "sp-list-item") {
      return node;
    }
  }
  return null;
}

function topLevelItemFromValueString(valueString) {
  if (valueString == null) {
    return null;
  }
  return (props.items ?? []).find((item) => {
    if (!item || item.header) {
      return false;
    }
    return String(getItemValue(item)) === valueString;
  });
}

function handleKeyDown(event) {
  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
    return;
  }
  const listItem = focusedListItemFromEvent(event);
  if (!listItem) {
    return;
  }
  const item = topLevelItemFromValueString(listItem.getAttribute("value"));
  if (!item?.hasChildren) {
    return;
  }
  const expandedNow = isExpanded(getItemValue(item));
  if (event.key === "ArrowRight" && !expandedNow) {
    event.preventDefault();
    event.stopPropagation();
    handleNext(item);
  } else if (event.key === "ArrowLeft" && expandedNow) {
    event.preventDefault();
    event.stopPropagation();
    handleNext(item);
  }
}
</script>

<style scoped lang="scss">
.sp-quantum-select-accordion-container {
  display: block;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>
